import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Avatar,
  Chip,
  IconButton,
  CardActions,
  LinearProgress,
  Container,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  LocationOn,
  AccessTime,
  DirectionsCar,
  Star,
  Phone,
  MyLocation,
  History,
  Payment,
  Cancel,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import { rideAPI } from '../services/api';
import { wsService } from '../services/websocket';
import { RIDER_EVENTS } from '../constants/websocket-events';
import { transformBackendRideToFrontend, useRideStore } from '../store/rideStore';
import { normalizeRideStatus, RideStatus } from '../constants/ride-status';

interface ActiveRide {
  id: string;
  status: RideStatus | string;
  driver?: {
    id: string;
    name: string;
    avatar?: string;
    rating: number;
    car: {
      make: string;
      model: string;
      color: string;
      plateNumber: string;
    };
    phone?: string;
  };
  origin: {
    address: string;
    latitude: number;
    longitude: number;
  };
  destination: {
    address: string;
    latitude: number;
    longitude: number;
  };
  estimatedTime?: number;
  estimatedFare?: number;
}

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSnackbar } = useSnackbar();
  const { updateCurrentRide } = useRideStore();
  const [activeRide, setActiveRide] = useState<ActiveRide | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);

  const updateActiveRideFromData = useCallback((rideData: any) => {
    const transformedRide = transformBackendRideToFrontend(rideData);
    if (transformedRide) {
      const normalizedStatus = normalizeRideStatus(transformedRide.status);

      if (
        normalizedStatus === RideStatus.CANCELLED || 
        normalizedStatus === RideStatus.COMPLETED ||
        normalizedStatus === RideStatus.NO_DRIVERS_AVAILABLE
      ) {
        console.log('Ride is cancelled/completed/no drivers, clearing active ride');
        setActiveRide(null);
        updateCurrentRide(null);
        return;
      }


      const activeRide: ActiveRide = {
        id: transformedRide.id.toString(),
        status: transformedRide.status,
        driver: transformedRide.driver ? {
          id: transformedRide.driver.id?.toString() || transformedRide.driver_id || '',
          name: transformedRide.driver.name || transformedRide.driver.full_name || 'Sürücü',
          avatar: transformedRide.driver.avatar || transformedRide.driver.profile_picture,
          rating: transformedRide.driver.rating || transformedRide.driver.average_rating || 0,
          car: {
            make: transformedRide.driver.vehicle?.make || transformedRide.driver.car?.make || '',
            model: transformedRide.driver.vehicle?.model || transformedRide.driver.car?.model || '',
            color: transformedRide.driver.vehicle?.color || transformedRide.driver.car?.color || '',
            plateNumber: transformedRide.driver.vehicle?.plate_number || transformedRide.driver.car?.plateNumber || '',
          },
          phone: transformedRide.driver.phone || transformedRide.driver.phone_number,
        } : undefined,
        origin: {
          address: transformedRide.start_point?.address || transformedRide.origin_address || '',
          latitude: transformedRide.start_point?.latitude || 0,
          longitude: transformedRide.start_point?.longitude || 0,
        },
        destination: {
          address: transformedRide.end_point?.address || transformedRide.destination_address || '',
          latitude: transformedRide.end_point?.latitude || 0,
          longitude: transformedRide.end_point?.longitude || 0,
        },
        estimatedFare: transformedRide.estimated_fare,
      };
      setActiveRide(activeRide);
    }
  }, [updateCurrentRide]);

  const fetchActiveRide = useCallback(async () => {
    try {
      const response = await rideAPI.getActiveRide();
      if (response.success && response.data) {

        const normalizedStatus = normalizeRideStatus(response.data.status || response.data.ride_status_config?.name || response.data.rideStatusConfig?.name);
        if (normalizedStatus === RideStatus.CANCELLED || normalizedStatus === RideStatus.COMPLETED) {
          console.log('Active ride is cancelled or completed, clearing');
          setActiveRide(null);
          updateCurrentRide(null);
        } else {
          updateActiveRideFromData(response.data);
        }
      } else {
        setActiveRide(null);
        updateCurrentRide(null);
      }
    } catch (error) {
      console.error('❌ Failed to fetch active ride:', error);
      setActiveRide(null);
      updateCurrentRide(null);
    } finally {
      setIsLoading(false);
    }
  }, [updateActiveRideFromData, updateCurrentRide]);

  useEffect(() => {
    fetchActiveRide();


    const setupWebSocket = async () => {
      try {
        await wsService.ensureConnection('rider');


        let updateTimeout: ReturnType<typeof setTimeout> | undefined;
        let lastUpdateRideId: string | null = null;

        const debouncedUpdate = (data: any) => {
          if (!data || !data.id) return;


          const normalizedStatus = normalizeRideStatus(data.status || data.ride_status_config?.name || data.rideStatusConfig?.name);
          if (normalizedStatus === RideStatus.CANCELLED || normalizedStatus === RideStatus.COMPLETED) {
            console.log('Ride update is cancelled/completed, clearing active ride');
            setActiveRide(null);
            updateCurrentRide(null);
            return;
          }

          const rideId = String(data.id);
          if (updateTimeout) {
            clearTimeout(updateTimeout);
          }


          if (lastUpdateRideId === rideId) {
            updateTimeout = setTimeout(() => {
              updateActiveRideFromData(data);
            }, 300); 
          } else {

            lastUpdateRideId = rideId;
            updateActiveRideFromData(data);
          }
        };

        const handleRideCreated = async (data: any) => {
          console.log('🔵 Ride created event received:', data);

          if (data && data.id) {
            updateActiveRideFromData(data);
          } else {

            await fetchActiveRide();
          }
        };

        const handleRideUpdated = (data: any) => {

          debouncedUpdate(data);
        };

        const handleRideStatusChanged = (data: any) => {

          debouncedUpdate(data);
        };

        const handleDriverRejected = (data: any) => {
          console.log('🔵 Driver rejected:', data);
          setActiveRide((prev: any) => {
            if (!prev) return prev;
            const dataRideId = data.ride_id || data.rideId || data.id;
            if (dataRideId === prev.id) {
              showSnackbar('Sürücü sifarişi qəbul etmədi. Başqa sürücü axtarılır...', 'warning');

              return {
                ...prev,
                status: 'searching',
                driver: undefined,
              };
            }
            return prev;
          });
        };

        const handleRideCancelled = (data: any) => {
          console.log('🔵 Ride cancelled event received:', data);
          const dataRideId = data.ride_id || data.rideId || data.id || data.ride?.id;
          setActiveRide((prev: any) => {
            if (!prev) return null;
            if (prev.id === dataRideId || prev.id?.toString() === dataRideId?.toString()) {

              updateCurrentRide(null);
              return null; 
            }
            return prev;
          });
        };

        const handleCurrentRide = (data: any) => {
          console.log('🔵 Current ride event received:', data);
          if (data && data.id) {
            updateActiveRideFromData(data);
          } else {
            fetchActiveRide();
          }
        };

        const handleDriverAssigned = (data: any) => {
          console.log('🔵 Driver assigned event received:', data);
          if (data && data.id) {
            updateActiveRideFromData(data);
          } else {
            fetchActiveRide();
          }
        };


        wsService.on(RIDER_EVENTS.RIDE_CREATED, handleRideCreated);
        wsService.on(RIDER_EVENTS.RIDE_UPDATED, handleRideUpdated);
        wsService.on(RIDER_EVENTS.RIDE_STATUS_CHANGED, handleRideStatusChanged);
        wsService.on(RIDER_EVENTS.RIDE_DRIVER_ASSIGNED, handleDriverAssigned);
        wsService.on(RIDER_EVENTS.RIDE_DRIVER_REJECTED, handleDriverRejected);
        wsService.on(RIDER_EVENTS.RIDE_CANCELLED, handleRideCancelled);
        wsService.on(RIDER_EVENTS.CURRENT_RIDE, handleCurrentRide);


        wsService.on('ride:driver_rejected', handleDriverRejected);
        wsService.on('driver:rejected', handleDriverRejected);
        wsService.on('ride:created', handleRideCreated);
        wsService.on('ride:updated', handleRideUpdated);
        wsService.on('ride:status_changed', handleRideStatusChanged);
        wsService.on('ride:driver_assigned', handleDriverAssigned);
        wsService.on('ride:cancelled', handleRideCancelled);
        wsService.on('current_ride', handleCurrentRide);

        return () => {

          if (updateTimeout) {
            clearTimeout(updateTimeout);
          }
          wsService.off(RIDER_EVENTS.RIDE_CREATED, handleRideCreated);
          wsService.off(RIDER_EVENTS.RIDE_UPDATED, handleRideUpdated);
          wsService.off(RIDER_EVENTS.RIDE_STATUS_CHANGED, handleRideStatusChanged);
          wsService.off(RIDER_EVENTS.RIDE_DRIVER_ASSIGNED, handleDriverAssigned);
          wsService.off(RIDER_EVENTS.RIDE_DRIVER_REJECTED, handleDriverRejected);
          wsService.off(RIDER_EVENTS.RIDE_CANCELLED, handleRideCancelled);
          wsService.off(RIDER_EVENTS.CURRENT_RIDE, handleCurrentRide);
          wsService.off('ride:driver_rejected', handleDriverRejected);
          wsService.off('driver:rejected', handleDriverRejected);
          wsService.off('ride:created', handleRideCreated);
          wsService.off('ride:updated', handleRideUpdated);
          wsService.off('ride:status_changed', handleRideStatusChanged);
          wsService.off('ride:driver_assigned', handleDriverAssigned);
          wsService.off('ride:cancelled', handleRideCancelled);
          wsService.off('current_ride', handleCurrentRide);
        };
      } catch (error) {
        console.error('WebSocket setup error:', error);
      }
    };

    setupWebSocket();


    let visibilityTimeout: ReturnType<typeof setTimeout> | undefined;
    let focusTimeout: ReturnType<typeof setTimeout> | undefined;

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        if (visibilityTimeout) {
          clearTimeout(visibilityTimeout);
        }
        visibilityTimeout = setTimeout(() => {
          fetchActiveRide();
        }, 500); 
      }
    };

    const handleWindowFocus = () => {
      if (focusTimeout) {
        clearTimeout(focusTimeout);
      }
      focusTimeout = setTimeout(() => {
        fetchActiveRide();
      }, 500); 
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
      if (visibilityTimeout) {
        clearTimeout(visibilityTimeout);
      }
      if (focusTimeout) {
        clearTimeout(focusTimeout);
      }
    };
  }, [fetchActiveRide, updateActiveRideFromData]);


  const handleTrackRide = () => {
    if (activeRide) {
      navigate(`/app/track/${activeRide.id}`);
    }
  };

  const handleCallDriver = () => {
    if (activeRide?.driver?.phone) {
      window.location.href = `tel:${activeRide.driver.phone}`;
    }
  };

  const handleCancelRideClick = () => {
    if (!activeRide) return;


    const normalizedStatus = normalizeRideStatus(activeRide.status);
    const cannotCancel = [
      RideStatus.COMPLETED,
      RideStatus.CANCELLED,
      RideStatus.STARTED,
    ].includes(normalizedStatus);

    if (cannotCancel) {
      showSnackbar('Bu sifarişi ləğv etmək mümkün deyil', 'warning');
      return;
    }

    setIsCancelDialogOpen(true);
  };

  const handleCancelRide = async () => {
    if (!activeRide) return;

    setIsCancelDialogOpen(false);

    try {
      await rideAPI.cancelRide(activeRide.id, 'İstifadəçi tərəfindən ləğv edildi');
      showSnackbar('Sifariş ləğv edildi', 'success');
      setActiveRide(null);

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error: any) {
      console.error('Cancel ride error:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Sifariş ləğv edilə bilmədi';
      showSnackbar(errorMessage, 'error');
    }
  };

  const getStatusText = (status: string | RideStatus) => {
    const normalizedStatus = normalizeRideStatus(status);
    switch (normalizedStatus) {
      case RideStatus.PENDING:
      case RideStatus.SEARCHING_DRIVER:
        return 'Sürücü axtarılır...';
      case RideStatus.DRIVER_ASSIGNED:
        return 'Sürücü sifarişi qəbul etdi';
      case RideStatus.DRIVER_ARRIVED:
        return 'Sürücü yoldadır';
      case RideStatus.STARTED:
        return 'Səyahət davam edir';
      case RideStatus.COMPLETED:
        return 'Tamamlandı';
      case RideStatus.CANCELLED:
        return 'Ləğv edildi';
      case RideStatus.NO_DRIVERS_AVAILABLE:
        return 'Sürücü tapılmadı';
      default:
        return normalizedStatus.toString();
    }
  };

  const getStatusColor = (status: string | RideStatus) => {
    const normalizedStatus = normalizeRideStatus(status);
    switch (normalizedStatus) {
      case RideStatus.PENDING:
      case RideStatus.SEARCHING_DRIVER:
        return 'warning';
      case RideStatus.DRIVER_ASSIGNED:
        return 'info';
      case RideStatus.DRIVER_ARRIVED:
        return 'primary';
      case RideStatus.STARTED:
        return 'success';
      case RideStatus.COMPLETED:
        return 'success';
      case RideStatus.CANCELLED:
      case RideStatus.NO_DRIVERS_AVAILABLE:
        return 'error';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ width: '100%', pt: 2 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3 }, px: { xs: 2, sm: 3 } }}>
      {}
      <Box sx={{ mb: { xs: 3, sm: 4 } }}>
        <Typography 
          variant="h4" 
          component="h1" 
          gutterBottom
          sx={{ 
            fontWeight: 700,
            background: 'linear-gradient(135deg, #FF006E 0%, #2196F3 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontSize: { xs: '1.75rem', sm: '2rem' },
          }}
        >
          Salam, {user?.name?.split(' ')[0] || 'İstifadəçi'}! 👋
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
        >
          Növbəti səyahətinizə hazır olun
        </Typography>
      </Box>

      {}
      {activeRide ? (
        <Card 
          sx={{ 
            mb: { xs: 3, sm: 4 },
            borderRadius: { xs: 3, sm: 4 },
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
            overflow: 'visible',
            border: '1px solid rgba(0, 0, 0, 0.05)',
          }}
        >
          <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
              <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                Aktiv Sifariş
              </Typography>
              <Chip
                label={getStatusText(activeRide.status)}
                color={getStatusColor(activeRide.status) as any}
                size="small"
                sx={{ fontWeight: 600 }}
              />
            </Box>

            {}
            {activeRide.driver && (
              <Card
                sx={{
                  mb: 2.5,
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'grey.200',
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box display="flex" alignItems="center">
                    <Avatar
                      src={activeRide.driver.avatar}
                      alt={activeRide.driver.name}
                      sx={{ 
                        width: { xs: 56, sm: 64 }, 
                        height: { xs: 56, sm: 64 }, 
                        mr: 2,
                        border: '3px solid',
                        borderColor: 'primary.main',
                      }}
                    />
                    <Box flexGrow={1}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {activeRide.driver.name}
                      </Typography>
                      <Box display="flex" alignItems="center" mb={0.5}>
                        <Star sx={{ color: 'warning.main', fontSize: { xs: 18, sm: 20 }, mr: 0.5 }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {activeRide.driver.rating}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        {activeRide.driver.car.make} {activeRide.driver.car.model} • {activeRide.driver.car.color} • {activeRide.driver.car.plateNumber}
                      </Typography>
                    </Box>
                    {activeRide.driver.phone && (
                      <IconButton 
                        color="primary" 
                        onClick={handleCallDriver}
                        sx={{ 
                          bgcolor: 'primary.main',
                          color: 'white',
                          minWidth: { xs: 48, sm: 56 },
                          minHeight: { xs: 48, sm: 56 },
                          padding: { xs: 1.5, sm: 2 },
                          touchAction: 'manipulation',
                          WebkitTapHighlightColor: 'transparent',
                          '&:hover': { bgcolor: 'primary.dark' },
                          '&:active': {
                            transform: 'scale(0.95)',
                            transition: 'transform 0.1s ease-out',
                          },
                        }}
                      >
                        <Phone />
                      </IconButton>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}

            {}
            <Stack spacing={2} sx={{ mb: 2.5 }}>
              <Box 
                sx={{ 
                  p: 2, 
                  bgcolor: 'grey.50', 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'grey.200',
                }}
              >
                <Box display="flex" alignItems="flex-start">
                  <LocationOn sx={{ color: 'primary.main', mr: 1.5, mt: 0.5, fontSize: { xs: 20, sm: 24 } }} />
                  <Box flexGrow={1}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Götürüləcək yer
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                      {activeRide.origin.address}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box 
                sx={{ 
                  p: 2, 
                  bgcolor: 'grey.50', 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'grey.200',
                }}
              >
                <Box display="flex" alignItems="flex-start">
                  <LocationOn sx={{ color: 'secondary.main', mr: 1.5, mt: 0.5, fontSize: { xs: 20, sm: 24 } }} />
                  <Box flexGrow={1}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Təyinat məntəqəsi
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                      {activeRide.destination.address}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Stack>

            {}
            {(activeRide.estimatedTime || activeRide.estimatedFare) && (
              <Box 
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  p: 2,
                  bgcolor: 'primary.main',
                  borderRadius: 2,
                  color: 'white',
                  mb: 2.5,
                }}
              >
                {activeRide.estimatedTime && (
                  <Box display="flex" alignItems="center">
                    <AccessTime sx={{ mr: 1, fontSize: { xs: 18, sm: 20 } }} />
                    <Typography variant="body1" sx={{ fontWeight: 600, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                      {activeRide.estimatedTime} dəqiqə
                    </Typography>
                  </Box>
                )}
                {activeRide.estimatedFare && (
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                    ₼{activeRide.estimatedFare.toFixed(2)}
                  </Typography>
                )}
              </Box>
            )}

            {}
            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleTrackRide}
                startIcon={<DirectionsCar />}
                size="large"
                sx={{ 
                  py: { xs: 2, sm: 2.5 },
                  borderRadius: 3,
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #FF006E 0%, #FF3399 100%)',
                  boxShadow: '0 6px 20px rgba(255, 0, 110, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #E1005F 0%, #E62E88 100%)',
                    boxShadow: '0 8px 24px rgba(255, 0, 110, 0.5)',
                  },
                  '&:active': {
                    transform: 'scale(0.98)',
                    transition: 'transform 0.1s ease-out',
                  },
                }}
              >
                Sifarişi İzlə
              </Button>
            </Box>
          </CardContent>

          <CardActions sx={{ p: { xs: 2, sm: 3 }, pt: 0, gap: 1, display: 'flex', justifyContent: 'center' }}>
            {(() => {
              const normalizedStatus = activeRide.status.toUpperCase();


              const canCancel = ![
                'ARRIVING',
                'IN_PROGRESS',
                'COMPLETED',
                'CANCELLED',
              ].includes(normalizedStatus);

              return canCancel ? (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleCancelRideClick}
                  startIcon={<Cancel />}
                  size="large"
                  fullWidth
                  sx={{ 
                    py: { xs: 1.5, sm: 2 },
                    borderRadius: 2,
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    fontWeight: 600,
                    touchAction: 'manipulation',
                    WebkitTapHighlightColor: 'transparent',
                    '&:active': {
                      transform: 'scale(0.98)',
                      transition: 'transform 0.1s ease-out',
                    },
                  }}
                >
                  Ləğv et
                </Button>
              ) : null;
            })()}
          </CardActions>
        </Card>
      ) : (
        <Card 
          sx={{ 
            mb: { xs: 3, sm: 4 },
            borderRadius: { xs: 3, sm: 4 },
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(0, 0, 0, 0.05)',
          }}
        >
          <CardContent sx={{ textAlign: 'center', py: { xs: 4, sm: 6 } }}>
            <Box
              sx={{
                width: { xs: 80, sm: 100 },
                height: { xs: 80, sm: 100 },
                borderRadius: '50%',
                bgcolor: 'grey.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <DirectionsCar sx={{ fontSize: { xs: 40, sm: 50 }, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
              Hazırda aktiv sifarişiniz yoxdur
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Yeni sifariş vermək üçün aşağıdakı düyməyə basın
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/app/order')}
              startIcon={<DirectionsCar />}
              sx={{ 
                px: 4,
                py: 1.5,
                borderRadius: 2,
                fontSize: { xs: '0.95rem', sm: '1rem' },
                fontWeight: 600,
              }}
            >
              Yeni Sifariş
            </Button>
          </CardContent>
        </Card>
      )}

      {}
      <Grid container spacing={2}>
        <Grid item xs={6} sm={4}>
          <Card
            sx={{
              borderRadius: { xs: 3, sm: 4 },
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
              border: '1px solid rgba(0, 0, 0, 0.05)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'transparent',
              minHeight: { xs: 120, sm: 140 },
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
              },
              '&:active': {
                transform: 'scale(0.98)',
                transition: 'transform 0.1s ease-out',
              },
            }}
            onClick={() => navigate('/app/order')}
          >
            <CardContent sx={{ textAlign: 'center', p: { xs: 2, sm: 3 } }}>
              <Box
                sx={{
                  width: { xs: 48, sm: 56 },
                  height: { xs: 48, sm: 56 },
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 1.5,
                }}
              >
                <MyLocation sx={{ color: 'white', fontSize: { xs: 24, sm: 28 } }} />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                Yeni Sifariş
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={4}>
          <Card
            sx={{
              borderRadius: { xs: 3, sm: 4 },
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
              border: '1px solid rgba(0, 0, 0, 0.05)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'transparent',
              minHeight: { xs: 120, sm: 140 },
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
              },
              '&:active': {
                transform: 'scale(0.98)',
                transition: 'transform 0.1s ease-out',
              },
            }}
            onClick={() => navigate('/app/history')}
          >
            <CardContent sx={{ textAlign: 'center', p: { xs: 2, sm: 3 } }}>
              <Box
                sx={{
                  width: { xs: 48, sm: 56 },
                  height: { xs: 48, sm: 56 },
                  borderRadius: '50%',
                  bgcolor: 'secondary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 1.5,
                }}
              >
                <History sx={{ color: 'white', fontSize: { xs: 24, sm: 28 } }} />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                Tarixçə
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={4}>
          <Card
            sx={{
              borderRadius: { xs: 3, sm: 4 },
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
              border: '1px solid rgba(0, 0, 0, 0.05)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'transparent',
              minHeight: { xs: 120, sm: 140 },
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
              },
              '&:active': {
                transform: 'scale(0.98)',
                transition: 'transform 0.1s ease-out',
              },
            }}
            onClick={() => navigate('/app/track')}
          >
            <CardContent sx={{ textAlign: 'center', p: { xs: 2, sm: 3 } }}>
              <Box
                sx={{
                  width: { xs: 48, sm: 56 },
                  height: { xs: 48, sm: 56 },
                  borderRadius: '50%',
                  bgcolor: '#FF006E',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 1.5,
                }}
              >
                <DirectionsCar sx={{ color: 'white', fontSize: { xs: 24, sm: 28 } }} />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                İzlə
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={4}>
          <Card
            sx={{
              borderRadius: { xs: 3, sm: 4 },
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
              border: '1px solid rgba(0, 0, 0, 0.05)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'transparent',
              minHeight: { xs: 120, sm: 140 },
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
              },
              '&:active': {
                transform: 'scale(0.98)',
                transition: 'transform 0.1s ease-out',
              },
            }}
            onClick={() => navigate('/app/payment')}
          >
            <CardContent sx={{ textAlign: 'center', p: { xs: 2, sm: 3 } }}>
              <Box
                sx={{
                  width: { xs: 48, sm: 56 },
                  height: { xs: 48, sm: 56 },
                  borderRadius: '50%',
                  bgcolor: 'success.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 1.5,
                }}
              >
                <Payment sx={{ color: 'white', fontSize: { xs: 24, sm: 28 } }} />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                Ödəniş
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {}
      <Dialog
        open={isCancelDialogOpen}
        onClose={() => setIsCancelDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: { xs: 3, sm: 4 },
            minWidth: { xs: '280px', sm: '400px' },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>
          Sifarişi ləğv et
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
            {activeRide && (activeRide.status === 'accepted' || activeRide.status === 'arriving')
              ? 'Sürücü təyin edilib. Sifarişi ləğv etmək istədiyinizə əminsiniz?'
              : 'Sifarişi ləğv etmək istədiyinizə əminsiniz?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 }, pt: 1 }}>
          <Button
            onClick={() => setIsCancelDialogOpen(false)}
            variant="outlined"
            sx={{
              minWidth: { xs: 100, sm: 120 },
              minHeight: { xs: 44, sm: 48 },
              touchAction: 'manipulation',
            }}
          >
            Xeyr
          </Button>
          <Button
            onClick={handleCancelRide}
            variant="contained"
            color="error"
            sx={{
              minWidth: { xs: 100, sm: 120 },
              minHeight: { xs: 44, sm: 48 },
              touchAction: 'manipulation',
            }}
          >
            Bəli
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default HomePage;
